import { Popover, Tag, Tooltip } from 'antd'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import useUserSkills from '../hooks/useUserSkills'
import { Skill } from '@lmc/ts-common/lib/rota/dtos/rota-patterns'
import { theme } from '@lmc/ui-kit'

type SkillsContainerProps = {
  skillsIds: Skill[]
}

const SkillsContainer = ({ skillsIds }: SkillsContainerProps) => {
  const { t } = useTranslation()

  const { allSkills } = useUserSkills()

  if (!skillsIds?.length) {
    return null
  }

  const skills: string[] = skillsIds
    .map((skillId: Skill) => {
      const skill = allSkills.find((skill) => skill?.id === skillId?.skillId)
      return skill ? skill.name : ''
    })
    .filter((skillName) => skillName !== '')

  const displayedSkills = skills?.slice(0, 3)
  const additionalSkillsCount = Math.max(skills.length - 3, 0)

  return (
    <StyledSkillsContainer>
      {displayedSkills?.map((skill) => (
        <Tooltip
          placement="bottom"
          color={theme.color.neutral.color3}
          title={
            <ContentContainer>
              <Text weight="medium" level={2}>
                {skill}
              </Text>
            </ContentContainer>
          }
        >
          <StyledTag key={skill}>
            <Text weight="medium" level={2}>
              {skill}
            </Text>
          </StyledTag>
        </Tooltip>
      ))}
      {additionalSkillsCount !== 0 && (
        <Popover
          placement="bottomLeft"
          showArrow
          content={
            <SkillsPopover>
              <Text weight="medium" level={2}>
                {t('SKILLS').toUpperCase()}
              </Text>
              {skills?.map((skill) => (
                <StyledTag key={skill}>
                  <Text weight="medium" level={2}>
                    {skill}
                  </Text>
                </StyledTag>
              ))}
            </SkillsPopover>
          }
          trigger="hover"
        >
          <ShowMoreText>{`+ ${additionalSkillsCount}`}</ShowMoreText>
        </Popover>
      )}
    </StyledSkillsContainer>
  )
}

const StyledTag = styled(Tag)`
  padding: 4px 8px;
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;

  &:hover {
    cursor: default;
  }
`

const StyledSkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`
const ShowMoreText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};

  &:hover {
    cursor: pointer;
  }
`
const SkillsPopover = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 4px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 8px;
  color: ${(props) => props.theme.color.neutral.color11};
`

export default SkillsContainer
